/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  type ChangeEvent,
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  CustomButtonGroup,
  DashboardLoader,
  // CustomInput,
  RadioButton,
  CustomInput,
  useAppSelector,
  useEffectOnce,
  useAppDispatch
} from '../../../globalUtils/globalExports'
import { FormLabel } from '@mui/material'
import { toast } from 'react-toastify'

// import { FormLabel } from '@mui/material'
import ChargerPanelSubNavListHeader from './ChargerPanelSubNavListHeader'
import React from 'react';
import hwAxios from '../../../rmsReduxStore/api-service/HWAxios'
import appUser from '../../../auth/service/AppUser'
import { fetchPeripheralCardRefData, fetchResetCounterRemoteCommandRefData, getChargerReducer, getReferenceDataReducer } from '../../../rmsReduxStore/reduxExports'
import { useLocation } from 'react-router-dom'
import SelectDropDown, { CustomDropdownType } from '../../../globalUtils/SelectDropDown/SelectDropDown'
import { checkValidForFTPString, keyPressValidationForFTPString } from '../../../globalUtils/globalHooks'
import { upperCase } from 'lodash'
import RemoteCommandExecutionLogs from './RemoteCommandExecutionLogs/RemoteCommandExecutionLogs'
import useRemoteCommandExecutionLogs from './RemoteCommandExecutionLogs/useRemoteCommandExecutionLogs'
import { EXECUTION_LOG_COMMANDS } from './RemoteCommandExecutionLogs/executionLogs.config'


interface ChargerPanelRemoteCommands      { buttonText: string, buttonIconClassName: string, buttonId: string }
interface ChargerPanelRemoteCommandsProps { contentHeight: number, commands: ChargerPanelRemoteCommands[]}
interface IConnectorOptions { radioButtonId: string, radioButtonLabel: string }

function ChargerPanelRemoteCommandsComponent({ contentHeight, commands }: ChargerPanelRemoteCommandsProps) {

  const dashboardContentRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const { resetCounterRemoteCommandReferenceData, peripheralCardReferenceData } = useAppSelector(getReferenceDataReducer);
  const dashboardNavRef = useRef<HTMLDivElement>(null)
  const [selectedNav, setSelectedNav] = useState<string | null>(null);
  const [urlValue, setUrlValue] = useState<string | null>(null);
  const [peripheralCardType, setPeripheralCardType] = useState<IPeripheralCardTypeReferenceData | null>(null);
  const [peripheralCardConnector, setPeripheralCardConnector] = useState<IGeneralReferenceData | null>(null);
  const [dashboardHeight, setDashboardHeight] = useState(0)
  const location = useLocation();
  const { chargerConnectorStatus, individualChargerDetail } = useAppSelector(getChargerReducer);
  const executionLogsCommand = selectedNav && EXECUTION_LOG_COMMANDS[individualChargerDetail?.productType ?? '']?.includes(selectedNav) ? selectedNav : null
  const executionLogsCommandLabel = useMemo(() => commands?.find((command) => command?.buttonId === selectedNav)?.buttonText ?? '', [commands, selectedNav])
  const {
    currentExecution,
    mostRecentExecution,
    isLogsLoaded,
    isExecutionInProgress,
    fetchExecutionLogs,
  } = useRemoteCommandExecutionLogs(location?.state?.chargerId ?? null, executionLogsCommand)

  // Perform stays blocked until the current execution of the selected command is finished
  const isPerformBlocked = !!executionLogsCommand && (!isLogsLoaded || isExecutionInProgress)
  const [connectorButtons, setConnectorButtons] = useState<IConnectorOptions[]>();
  const [errorMessage, setErrorMessage]= useState('');
  const BOTH_CONNECTORS_ID = 'BOTH_CONNECTORS';



  useEffect(() => {
    if (commands.length > 0) {
      setSelectedNav(commands[0].buttonId);
    } 
  }, [commands]);

  useEffectOnce(()=>{
    dispatch(fetchResetCounterRemoteCommandRefData());
    dispatch(fetchPeripheralCardRefData({chargerId: location?.state?.chargerId}));
  })

  useEffect(() => {
    if (chargerConnectorStatus) {
      const connectorOptions: IConnectorOptions[] = chargerConnectorStatus.map((connector) => { return { radioButtonId: connector.connectorId, radioButtonLabel: '' + connector.connectorName + ' (' + connector.connectorType + ')' } as IConnectorOptions; });
      setConnectorButtons(connectorOptions);
    }
  }, [chargerConnectorStatus])

  const [selectedConnectorValue, setSelectedConnectorValue] = useState<string | null>(null);
  const [bothConnectors,setBothConnectors] = useState<boolean | null>(null);
  const bothConnectorsRef = useRef(bothConnectors);

  const handleConnectorChange = useCallback((event: ChangeEvent<HTMLInputElement>, value: string): void => {
    if (value === BOTH_CONNECTORS_ID) {
      setBothConnectors(true);
      bothConnectorsRef.current = true;
    } else {
      setSelectedConnectorValue(value);
      setBothConnectors(false);
      bothConnectorsRef.current = false;
    }
  }, []);

  const handleNavBtnClick = useCallback((event: MouseEvent<HTMLButtonElement>, val: string) => {
    setSelectedNav(val);
  }, [])

  const handleUrlChange = useCallback((event) => {
    if(checkValidForFTPString(event?.target?.value)){
         setErrorMessage('');
    }
    else{
      setErrorMessage('Please enter valid value.')
    }
    setUrlValue(checkValidForFTPString(event?.target?.value))
  }, [])

  const calculateContentHeight = useCallback(() => {
    if (
      dashboardNavRef.current != null &&
      dashboardContentRef.current != null
    ) {
      const dashboardNavRect =
        dashboardNavRef.current.getBoundingClientRect()

      const navbarHeight = dashboardNavRect.height

      const remainingHeight = contentHeight - navbarHeight - 10
      setDashboardHeight(remainingHeight)
    }
  }, [contentHeight])

  useEffect(() => {
    calculateContentHeight()
    window.addEventListener('resize', calculateContentHeight)
    return (): void => {
      window.removeEventListener('resize', calculateContentHeight)
    }
  }, [calculateContentHeight])

  const isFtpUrlRequired = useMemo(() => {
     return selectedNav && 
     (['ftp_software_upgrade','ftp_log_upload','ftp_certificate_upgrade', 'ftp_screen_saver_images_upgrade',
     'ftp_customer_logo_update','ftp_config_file_update','plc_software_upgrade','ftp_peripheral_card_sw_update'].indexOf(selectedNav) != -1);
   },[selectedNav])

   const isConnectorRequired = useMemo(() => {
    return selectedNav && (selectedNav === 'disable_connector' || selectedNav === 'enable_connector' || selectedNav === 'calibrate_voltage' || selectedNav === 'plc_software_upgrade' || selectedNav === 'plc_reset')
  },[selectedNav])

  const isResetCountDropDownRequired = useMemo(() => {
    return selectedNav && (selectedNav === 'reset_counter')
  },[selectedNav])

  const isPeripheralCardDropDownRequired = useMemo(() => {
    return selectedNav && (selectedNav === 'ftp_peripheral_card_sw_update' || selectedNav === 'card_reset')
  },[selectedNav])

  const isBothConnectorsRequired = useMemo (()=>{
    return selectedNav && (selectedNav === 'plc_software_upgrade' || selectedNav === 'plc_reset')
  },[selectedNav])

  const onPerformBtnClick = useCallback(() => {

    if (isPerformBlocked) {
      toast.error('This command is already being executed on this charger.');
      return;
    }
    // Validate:
    if (isConnectorRequired && !selectedConnectorValue && !bothConnectors) {
        toast.error('Please select the connector.');
        return;
    }
    if (isFtpUrlRequired && (!urlValue || !urlValue.trim())) {
       toast.error('Please enter the URL.');
       return;
    }
    if(isResetCountDropDownRequired &&  (!urlValue || !urlValue.trim())){
      toast.error('Please select the command.');
      return;
    }

    if(isPeripheralCardDropDownRequired && (!peripheralCardType || !peripheralCardType?.id.trim())) {
      toast.error('Please select the card type.')
      return;
    }

    setShowLoader(true);
    const url = process.env.REACT_APP_SERVER_BASE_URL;
    const body = {
      remoteCommand: selectedNav, userId: appUser.user?.id,
      chargerId: location.state.chargerId,
      connectorId: selectedConnectorValue,
    }
    if (urlValue) { body['ftpUrl'] = urlValue; }
    if (selectedConnectorValue) { body['connectorId'] = selectedConnectorValue }
    if (bothConnectorsRef?.current) { body['bothConnectors'] = bothConnectorsRef?.current}
    if (peripheralCardType && (selectedNav === 'ftp_peripheral_card_sw_update' || selectedNav === 'card_reset')) { 
      body['peripheralCardType'] = peripheralCardType?.id;
      if(peripheralCardType?.selector === 'connector') {
        body['connectorId'] = !peripheralCardConnector?.id || peripheralCardConnector?.id == 'all' ? null : peripheralCardConnector?.id;
      } else if (peripheralCardType?.selector === 'smr') {
        body['connectorId'] = null;
        body['smrNo'] = !peripheralCardConnector?.id || peripheralCardConnector?.id == 'all' ? null : Number(peripheralCardConnector?.id);
      } else {
        body['connectorId'] = null;
        body['smrNo'] = null;
      }
    }

    hwAxios.post(url + '/charger/remote-command', body).then((response) => {
      setShowLoader(false);
      const message = response.data.message;
      if (response.data.statusCode == 200) {
        toast.success(message);
      } else {
        toast.error(message);
      }
    }).catch((error) => {
      console.log('Error Message', JSON.stringify(error));
      setShowLoader(false);
      toast.error('Something went wrong. Please try again.');
    }).finally(() => {
      // The execution log is created whether the command succeeds or fails, so always refresh it
      if (executionLogsCommand) fetchExecutionLogs();
    })
  }, [selectedNav, appUser, location, urlValue, selectedConnectorValue, peripheralCardType, peripheralCardConnector, isPerformBlocked, executionLogsCommand, fetchExecutionLogs])

  const btnsList = useMemo(() => {
    return [
      {
        buttonText: 'Perform',
        buttonId: 'perform',
        btnClassName: 'primary__btn',
        handleClick: onPerformBtnClick,
        isDisabled: isPerformBlocked,
        buttonVariant: 'filled'
      }
    ]
  }, [onPerformBtnClick, isPerformBlocked])

  const [showLoader, setShowLoader] = useState(false);

  const bothConnectorButtons = useMemo<IConnectorOptions[] | undefined>(() => {
  if (!connectorButtons) return connectorButtons;

  if (isBothConnectorsRequired && connectorButtons.length > 1) {
    return [
      ...connectorButtons,
      {
        radioButtonId: BOTH_CONNECTORS_ID,
        radioButtonLabel: `Both Connectors (${connectorButtons
          .map(c => c.radioButtonLabel)
          .join(' & ')})`
      }
    ];
  }

  return connectorButtons;
}, [connectorButtons, isBothConnectorsRequired]);

const handlePeripheralCardTypeChange = (val) => {
  setPeripheralCardConnector(null);
  setPeripheralCardType(val);
}

const handlePeripheralCardConnectorChange = (val) => {
  setPeripheralCardConnector(val);
}


  useEffect(() => {
    if (selectedNav && (selectedNav.indexOf('ftp') != -1)) {
      setUrlValue('');
    }
    if (selectedNav && (selectedNav.indexOf('connector') != -1) && connectorButtons) {
      setSelectedConnectorValue(connectorButtons!.length > 0 ? connectorButtons[0].radioButtonId : null);
    }
  }, [selectedNav, connectorButtons])

  // Target selected on the screen, e.g. 'Connector 1', 'All SMRs', 'Charger'
  const peripheralCardSelector = isPeripheralCardDropDownRequired ? peripheralCardType?.selector : null
  const executionLogsTargetLabel = !peripheralCardSelector ? 'Charger'
    : !peripheralCardConnector?.id || peripheralCardConnector?.id === 'all' ? (peripheralCardSelector === 'smr' ? 'All SMRs' : 'All Connectors')
      : peripheralCardConnector?.name

  return (
    <div className='charger__remote__commands_dashboard' style={{ height: `${contentHeight - 10}px` }} >
      <DashboardLoader handleClose={setShowLoader} showLoader={showLoader} />
      {selectedNav && <ChargerPanelSubNavListHeader handleNavBtnClick={handleNavBtnClick} selectedNav={selectedNav!}
        dashboardNavRef={dashboardNavRef} navList={commands}
      />}

      <div ref={dashboardContentRef} className='charger__remote__commands_dashboard-content-wrap'
        style={{ height: `${dashboardHeight - 10}px`, overflowY: 'auto' }}
      >
        { isConnectorRequired && (<div>
          <div className="charger__remote__commands_dashboard-radio-buttons-wrap">
            <RadioButton
              key={selectedConnectorValue || 'radio-button-value-remote'}
              buttonsList={isBothConnectorsRequired ? bothConnectorButtons : connectorButtons}
              handleSelectedRadioBtnChange={handleConnectorChange}
              selectedRadioBtnValue={bothConnectors ? BOTH_CONNECTORS_ID : selectedConnectorValue}
              radionBtnGrpName={'charger-remote-commands-dashboard-radio-buttons'}
            />
          </div>
        </div>)}

        {isFtpUrlRequired && (<div>
          <div className="charger__remote__commands_dashboard-content">
            <FormLabel className={'rms-form-label'} required>
              URL
            </FormLabel>
            <CustomInput
              inputPlaceHolder="Type here"
              handleInputChange={handleUrlChange}
              inputValue={urlValue || ''}
              inputFieldId="adding_station"
              isRequired={true}
              handleKeyPress={keyPressValidationForFTPString}
              errorMessage={errorMessage}
            />
          </div>
        </div>)}


        { isPeripheralCardDropDownRequired  &&
        <div className='remote__command__ftp__peripheral__card__container'>
          <div className='remote__command__ftp__peripheral__card__dropdown-wrap'>
              <FormLabel className={'rms-form-label'} required>
                Card Type
              </FormLabel>
              <div className="remote__command__ftp__peripheral__card__dropdown">
                <SelectDropDown
                selectValue={peripheralCardType}
                selectOptions={peripheralCardReferenceData?.cardTypes ?? []}
                handleSelectOptionChange={handlePeripheralCardTypeChange}
                selectDropDownId={'peripheral_card_type'}
                isMultiSelect={false}
              />
              </div>
          </div>
          {
            peripheralCardType?.selector != null && <div className='remote__command__ftp__peripheral__card__dropdown-wrap'>
              <FormLabel className={'rms-form-label'} required>
                {upperCase(peripheralCardType?.selector)}
              </FormLabel>
              <div className="remote__command__ftp__peripheral__card__dropdown">
                <SelectDropDown
                selectValue={peripheralCardConnector}
                selectOptions={peripheralCardReferenceData?.[peripheralCardType?.selector] ?? []}
                handleSelectOptionChange={handlePeripheralCardConnectorChange}
                selectDropDownId={'peripheral_card_connector'}
                isMultiSelect={false}
              />
              </div>
          </div>
          }
        </div>  
        }

        { isResetCountDropDownRequired  &&
         <div>
             <FormLabel className={'rms-form-label'} required>
             Counter Type
            </FormLabel>
                 <div className="charger__remote__commands_dashboard-content">
                 <SelectDropDown
                 selectValue={''}
                 selectOptions={resetCounterRemoteCommandReferenceData ?? []}
                 handleSelectOptionChange={(option)=>{    setUrlValue(option?.value)}}
                 selectDropDownId={'reset_counter'}
                 isMultiSelect={false}
              />
                  </div>
         </div>
        }

        <div className='charger__remote__commands_dashboard-footer-wrap'>
          <CustomButtonGroup buttonsList={btnsList} buttonGroupClassName={'button__group__footer'} />
        </div>

        {executionLogsCommand &&
          <RemoteCommandExecutionLogs
            currentExecution={currentExecution}
            mostRecentExecution={mostRecentExecution}
            isLogsLoaded={isLogsLoaded}
            commandLabel={executionLogsCommandLabel}
            targetLabel={executionLogsTargetLabel}
          />
        }
      </div>
    </div>
  )
}

export default ChargerPanelRemoteCommandsComponent
